import { Alert, Button, Input, message, Typography } from 'antd';
import { useCallback, useState } from 'react';
import { UserHttp } from '@/services';
import { storage } from '@/utils/storage';
import { Flex, PasswordRequirementsGuide } from '../common';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import { usePasswordConfig } from '@/hooks';
import type { PasswordValidationResult } from '@/types/passwordConfig';

export default function ForceChangePasswordModal() {
  const [t] = useTranslation(['common']);
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatNewPassword, setRepeatNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidationResult | null>(null);

  const token = storage.get('TOKEN', null);
  const user = token ? jwtDecode(token.toString()) as { user_name?: string } : null;

  const { config, requirements, validatePassword } = usePasswordConfig(user?.user_name);

  const validateForm = useCallback(() => {
    const validation = validatePassword(newPassword || '');
    setPasswordValidation(validation);
    return validation.isValid;
  }, [validatePassword, newPassword]);

  const handleNewPasswordChange = useCallback((value: string) => {
    setNewPassword(value);
    const validation = validatePassword(value);
    setPasswordValidation(validation);
  }, [validatePassword]);

  const inputClassname = 'text-sm h-[40px]';

  const handleUpdate = useCallback(async () => {
    try {
      setError('');
      if (password.trim() === newPassword.trim()) {
        setError(t('common:errors.same-password'));
        return;
      }
      const isValid = validateForm();
      if (isValid) {
        setLoading(true);
        await UserHttp.changeMePassword({
          password,
          newPassword,
          repeatNewPassword,
        });
        storage.remove('TOKEN');
        storage.remove('REFRESH_TOKEN');
        message.success({ content: t('common:messages.success-password-change') });
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    }
    catch (e: any) {
      setError(e?.response?.data?.messageFa ?? t('common:errors.connection-error-try-again'));
    }
    finally {
      setLoading(false);
    }
  }, [password, repeatNewPassword, newPassword, t, validateForm]);

  const isDisabled =
    password.trim() === '' ||
    repeatNewPassword.trim() === '' ||
    newPassword.trim() === '' ||
    newPassword != repeatNewPassword;

  const showPasswordErrors =
    newPassword.length > 0 &&
    passwordValidation?.errors &&
    passwordValidation.errors.length > 0;

  return (
    <Flex gap={12} direction="column">
      <Flex className="font-bold text-base mb-2">
        {t('common:labels.update')} {t('common:labels.password')}
      </Flex>
      <Alert
        className="mb-2"
        type="warning"
        message={(
          <div className="text-sm">
            {t('common:messages.expired-password')}
          </div>
        )}
      />
      <Input.Password
        size="middle"
        value={password}
        placeholder={t('common:labels.current-password')}
        className={inputClassname}
        onChange={(e) => setPassword(e.target.value)} />
      <div>
        <Input.Password
          size="middle"
          value={newPassword}
          placeholder={t('common:labels.new-password')}
          className={inputClassname}
          onChange={(e) => handleNewPasswordChange(e.target.value)} />
        <PasswordRequirementsGuide config={config} requirements={requirements} />
        {showPasswordErrors && (
          <div className="password-errors-list">
            {passwordValidation.errors.map((errorMessage, index) => (
              <div key={index} className="text-red-500 text-xs mt-1">
                {errorMessage}
              </div>
            ))}
          </div>
        )}
      </div>
      <Input.Password
        size="middle"
        value={repeatNewPassword}
        placeholder={t('common:labels.confirm-new-password')}
        className={inputClassname}
        onChange={(e) => setRepeatNewPassword(e.target.value)} />
      {error && <Typography.Text type="danger">{error}</Typography.Text>}
      <Button
        loading={loading}
        type="primary"
        className="ms-auto"
        disabled={isDisabled}
        onClick={handleUpdate}>
        {t('common:labels.update')}
      </Button>
    </Flex>
  );
}
